import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import WorkSummariesCard from "./WorkSummariesCard";
import TabFreshnessNote from "./TabFreshnessNote";

export default function NotesTab({ client }) {
  const [notes, setNotes] = useState([]);
  const [digestLogs, setDigestLogs] = useState([]);
  const [newNote, setNewNote] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [n, d] = await Promise.all([
      api.get(`/notes/${client.id}`),
      api.get(`/scheduler/digest-logs/${client.id}`).catch(() => ({ data: { logs: [] } })),
    ]);
    setNotes(n.data);
    setDigestLogs(d.data?.logs || []);
  };

  useEffect(() => { load(); }, [client.id]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.title.trim() || !newNote.body.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/notes", {
        client_id: client.id,
        title: newNote.title,
        body: newNote.body,
      });
      setNotes((prev) => [data, ...prev]);
      setNewNote({ title: "", body: "" });
      toast.success("Note added.");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/notes/${id}`);
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  const inputCls = "w-full bg-white border border-[#e2e8f0] rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all";

  return (
    <div className="space-y-6" data-testid="notes-tab">
      <TabFreshnessNote testid="notes-freshness">
        Monthly digest auto-sent on the 1st at 10:30 UTC (includes last month&apos;s work summary) · Notes never expire.
      </TabFreshnessNote>

      {/* Monthly work summary card — surfaces in the PDF + email digest */}
      <WorkSummariesCard clientId={client.id} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Notes column */}
      <div className="space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">notes</div>
          <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Client notes</h3>
        </div>

        <form onSubmit={addNote} className="card-soft p-5 space-y-3">
          <input
            value={newNote.title}
            onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
            placeholder="Note title…"
            data-testid="note-title-input"
            className={inputCls}
          />
          <textarea
            value={newNote.body}
            onChange={(e) => setNewNote({ ...newNote, body: e.target.value })}
            placeholder="Write a note about this client…"
            rows={3}
            data-testid="note-body-input"
            className={`${inputCls} resize-none`}
          />
          <button
            type="submit"
            disabled={saving}
            data-testid="note-add-button"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" /> Add note
          </button>
        </form>

        <div className="space-y-2.5">
          {notes.length === 0 && (
            <div className="text-sm text-[#a0aec0] text-center py-6">No notes yet.</div>
          )}
          {notes.map((n) => (
            <div key={n.id} className="card-soft p-5" data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between">
                <div className="font-semibold text-sm text-[#1a202c]">{n.title}</div>
                <button
                  onClick={() => deleteNote(n.id)}
                  data-testid={`delete-note-${n.id}`}
                  className="text-[#a0aec0] hover:text-[#f56565]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="text-sm text-[#4a5568] mt-2 whitespace-pre-wrap">{n.body}</div>
              <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-3">
                {new Date(n.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Digest log column */}
      <div className="space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">monthly digest</div>
          <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Digest log</h3>
        </div>

        <div className="card-soft overflow-hidden" data-testid="digest-log-panel">
          <div className="px-5 py-3.5 border-b border-[#edf2f7] flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-[#a0aec0]" />
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Digest log</div>
            <div className="ml-auto text-[10px] text-[#a0aec0] font-mono-tight">
              {digestLogs.length} attempt(s)
            </div>
          </div>
          {digestLogs.length === 0 ? (
            <div className="p-4 text-xs text-[#a0aec0]">
              No digest emails sent yet. Auto-sent on the 1st of every month at 10:30 UTC.
            </div>
          ) : (
            <div className="divide-y divide-[#edf2f7] max-h-56 overflow-y-auto">
              {digestLogs.map((l) => (
                <div
                  key={l.id}
                  data-testid={`digest-log-${l.id}`}
                  className="px-5 py-2.5 flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {l.status === "sent" ? (
                      <div className="h-7 w-7 rounded-lg bg-[#48bb78]/15 flex items-center justify-center shrink-0">
                        <CheckCircle2 className="h-3.5 w-3.5 text-[#48bb78]" />
                      </div>
                    ) : (
                      <div className="h-7 w-7 rounded-lg bg-[#f56565]/15 flex items-center justify-center shrink-0">
                        <AlertCircle className="h-3.5 w-3.5 text-[#f56565]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-mono-tight text-[#1a202c] truncate">{l.recipient}</div>
                      {l.error && (
                        <div className="text-[10px] text-[#f56565] truncate font-mono-tight">{l.error}</div>
                      )}
                    </div>
                  </div>
                  <div className="font-mono-tight text-[#a0aec0] shrink-0 ml-3">
                    {new Date(l.at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
